<template>
  <div
    v-if="show && !hasPurchased"
    class="upgrade-widget-container"
    :style="backgroundStyle"
  >
    <div
      class="upgrade-widget-component"
    >
      <img
        v-if="featuredAttachment"
        :src="featuredAttachment.url"
        class="featured-attachment"
        alt=""
      />
      <header-text
        v-if="jsonConfig.title"
        :size="'xs'"
        class="header-text"
      >
        {{ jsonConfig.title }}
      </header-text>
      <hlt-text
        v-if="jsonConfig.description"
        class="description-text"
      >
        {{ jsonConfig.description }}
      </hlt-text>
      <button
        class="upgrade-action"
        @click="handleUpgradeAction"
      >
        <icon
          :name="'lock'"
          :variant="'white'"
          class="lock-icon"
        />
        {{ jsonConfig.buttonText || "Upgrade Now" }}
      </button>
    </div>
    <div
      class="upgrade-widget-image"
      :style="forgroundImageStyle"
    >
    </div>
  </div>
</template>

<script>
import HeaderText from '@/components/ui/header-text.vue';
import HltText from '@/components/ui/text.vue';
import Icon from '@/components/singletons/icon.vue';

export default {
  components: { HeaderText, HltText, Icon },
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      show: false,
    };
  },
  computed: {
    backgroundAttachment() {
      if (this.jsonConfig.webBackgroundAttachmentId) {
        return this.$store.getters['attachments/get'][this.jsonConfig.webBackgroundAttachmentId];
      }
    },
    foregroundAttachment() {
      if (this.jsonConfig.webForegroundAttachmentId) {
        return this.$store.getters['attachments/get'][this.jsonConfig.webForegroundAttachmentId];
      }
    },
    forgroundImageStyle() {
      if (this.foregroundAttachment) {
        return {
          background: `url(${this.foregroundAttachment.url})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        };
      } else {
        return {display: 'none'};
      }
    },
    backgroundStyle() {
      if (this.backgroundAttachment) {
        return {
          background: `url(${this.backgroundAttachment.url})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        };
      } else {
        return {};
      }
    },
    catalogPageFromPurchaseOrder() {
      const purchaseOrderId = this.jsonConfig.purchaseOrderId;
      if (purchaseOrderId) {
        const catalogPages = this.$store.getters['catalog/pageList'];
        return catalogPages.find(page => page.purchaseOrderId === Number(purchaseOrderId));
      }
    },
    featuredAttachment() {
      if (this.jsonConfig.iconAttachmentId) {
        return this.$store.getters['attachments/get'][this.jsonConfig.iconAttachmentId];
      }
    },
    hasPurchased() {
      if (this.isCatalogPurchaseOrder) {
        const activePurchaseOrders = this.$store.getters['purchaseReceipts/active'].map(pr => pr.purchaseOrderId);
        const catalogPurchaseOrders = this.$store.getters['purchaseOrders/all']
          .filter(po => po.parentId === this.jsonConfig.purchaseOrderId && !po.isFree)
          .map(po => po.id);
        return catalogPurchaseOrders.some(poId => activePurchaseOrders.includes(poId));
      } else {
        return this.$store.getters['session/hasPaid'];
      }
    },
    isCatalogPurchaseOrder() {
      return !!this.catalogPageFromPurchaseOrder;
    },
    jsonConfig() {
      return this.config?.config || {};
    },
  },
  mounted() {
    const user = this.$store.getters['user/get'];
    if (user.previewer) {
      this.show = true;
    } else if (this.isCatalogPurchaseOrder && !user.catalogPricingEnabled) {
      this.show = false;
    } else {
      const widgetDisabled = this.$apptimize.checkExperiments({
        experimentName: 'Buy Widget',
        bool: 'upgradeWidgetDisabled',
        defaultVal: false,
        matcher: /Buy Widget/,
      });
      if (!widgetDisabled) {
        this.show = true;
      }
    }
    this.fetchAttachments();
  },
  methods: {
    fetchAttachments() {
      let attachmentIds = [];
      if (this.jsonConfig?.webBackgroundAttachmentId) {
        attachmentIds.push(this.jsonConfig.webBackgroundAttachmentId);
      }
      if (this.jsonConfig?.iconAttachmentId) {
        attachmentIds.push(this.jsonConfig.iconAttachmentId);
      }
      if (this.jsonConfig?.webForegroundAttachmentId) {
        attachmentIds.push(this.jsonConfig.webForegroundAttachmentId);
      }
      if (attachmentIds.length > 0) {
        this.$store.dispatch('attachments/fetch', { ids: attachmentIds });
      }
    },
    handleUpgradeAction() {
      const params = {
        widgetId: this.config.id,
        widgetTitle: this.config.name,
      };
      this.$analytics.events.tappedBuyWidget(params);

      if (this.catalogPageFromPurchaseOrder) {
        this.$store.dispatch('ui/toggleCatalogModal', {
          show: true,
          catalogPageId: this.catalogPageFromPurchaseOrder.id,
          source: {
            type: 'Buy Widget',
            value: this.config?.id,
          },
        });
      } else {
        this.$store.dispatch('ui/toggleUpsellModal', {
          show: true,
          source: {
            type: 'Buy Widget',
            value: this.config?.id,
          },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.upgrade-widget-container {
  display: flex;

  @include breakpoint(medium) {
    background: none !important;
  }

  .upgrade-widget-image {
    min-width: rem-calc(208);
    height: rem-calc(208);
    @include breakpoint(medium) {
      display: none !important;
    }
  }

  .upgrade-widget-component {
    width: 100%;
    align-items: left;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 rem-calc(32);
    position: relative;
    margin-bottom: rem-calc(16);

    @include breakpoint(medium) {
      align-items: center;
      justify-content: center;
    }

    .featured-attachment {
      display: none;
      @include breakpoint(medium) {
        margin-bottom: rem-calc(16);
        display: flex !important;
      }
    }



    .header-text {
      margin-bottom: rem-calc(8);
    }

    .description-text {
      text-align: left;
      margin-bottom: rem-calc(16);
      @include breakpoint(medium) {
        text-align: center;
      }
    }

    .upgrade-action {
      background-color: #FF8A1A;
      border-radius: 112px;
      box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
      color: #ffffff;
      font-weight: $semi-bold;
      padding: rem-calc(12) rem-calc(24);
      margin-right: auto;
      @include breakpoint(medium) {
        margin-right: 0;
      }

      .lock-icon {
        height: rem-calc(12);
        margin-right: rem-calc(8);
        width: rem-calc(12);
      }
    }
  }
}
</style>
